import { createAction, createReducer, createSelector } from "@reduxjs/toolkit"
import { api } from "./helpers/api"

import i18n from "../i18n"
import { toaster } from "components/common/Toast"
import { serialize } from "object-to-formdata"
import { pick } from "lodash"

import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

const requested = createAction("user/requested")
const received = createAction("user/received")
export const updated = createAction("user/updated")
export const emailConfirmationReceived = createAction("user/emailConfirmationReceived")
const statusUpdated = createAction("user/statusUpdated")
const forceStatusUpdated = createAction("user/forceStatusUpdated")
export const cleaned = createAction("user/cleaned")
const failed = createAction("user/failed")
const guideUpdated = createAction("user/guideUpdated")
const guideUnavailableWeekdaysUpdated = createAction("user/guideUnavailableWeekdaysUpdated")
const passwordUpdated = createAction("user/passwordUpdated")

const userVerified = createAction("user/verifiedPhone")
const userVerificationError = createAction("user/verificationError")

const stripeDisconnected = createAction("user/stripeDisconnected")

export const setSelectedPaymentMethodId = createAction("user/setSelectedPaymentMethodId")
export const setSelectedCardLast4 = createAction("user/setSelectedCardLast4")
export const requestedPaymentMethods = createAction("user/requestedPaymentMethods")
const receivedPaymentMethods = createAction("user/receivedPaymentMethods")
export const failedPaymentMethods = createAction("user/failedPaymentMethods")
export const cleanedPaymentMethods = createAction("user/cleanedPaymentMethods")
const oAuthDisconnected = createAction("user/oAuthDisconnected")

const sendOtpToVerifyPhoneRequested = createAction("user/sendOtpToVerifyPhoneRequested")
const sendOtpToVerifyPhoneReceived = createAction("user/sendOtpToVerifyPhoneReceived")
const sendOtpVerifyPhoneFailed = createAction("user/sendOtpVerifyPhoneFailed")

const verifyOtpForPhoneNumberUpdationRequested = createAction("user/verifyOtpForPhoneNumberUpdationRequested")
const verifyOtpForPhoneNumberUpdationReceived = createAction("user/verifyOtpForPhoneNumberUpdationReceived")
const verifyOtpForPhoneNumberUpdationFailed = createAction("user/verifyOtpForPhoneNumberUpdationFailed")

const sendOtpToVerifyEmailRequested = createAction("user/sendOtpToVerifyEmailRequested")
const sendOtpToVerifyEmailReceived = createAction("user/sendOtpToVerifyEmailReceived")
const sendOtpVerifyEmailFailed = createAction("user/sendOtpVerifyEmailFailed")

const verifyOtpForEmailUpdationRequested = createAction("user/verifyOtpForEmailUpdationRequested")
const verifyOtpForEmailUpdationReceived = createAction("user/verifyOtpForEmailUpdationReceived")
const verifyOtpForEmailUpdationFailed = createAction("user/verifyOtpForEmailUpdationFailed")

const sendOtpForAccountActionRequested = createAction("user/sendOtpForAccountActionRequested")
const sendOtpForAccountActionReceived = createAction("user/sendOtpForAccountActionReceived")
const sendOtpForAccountActionFailed = createAction("user/sendOtpForAccountActionFailed")

const updateUserInfoRequested = createAction("user/updateUserInfoRequested")
const updateUserInfoReceived = createAction("user/updateUserInfoReceived")
const updateUserInfoFailed = createAction("user/updateUserInfoFailed")

const checkUserDeleteAbleRequested = createAction("user/checkUserDeleteAbleRequested")
const checkUserDeleteAbleReceived = createAction("user/checkUserDeleteAbleReceived")
const checkUserDeleteAbleFailed = createAction("user/checkUserDeleteAbleFailed")

const deleteAccountRequested = createAction("user/deleteAccountRequested")
const deleteAccountReceived = createAction("user/deleteAccountReceived")
const deleteAccountFailed = createAction("user/deleteAccountFailed")

const requestedDetachPaymentMethods = createAction("user/requestedDetachPaymentMethods")
const receivedDetachPaymentMethods = createAction("user/receivedDetachPaymentMethods")
const failedDetachPaymentMethods = createAction("user/failedDetachPaymentMethods")

export const updateGuideAccountStatusRequested = createAction("user/updateGuideAccountStatusRequested")
export const updateGuideAccountStatusReceived = createAction("user/updateGuideAccountStatusReceived")
export const updateGuideAccountStatusFailed = createAction("user/updateGuideAccountStatusFailed")

export const scheduleDeletionRequested = createAction("user/scheduleDeletionRequested")
export const scheduleDeletionReceived = createAction("user/scheduleDeletionReceived")
export const scheduleDeletionFailed = createAction("user/scheduleDeletionFailed")

export const cancelScheduledDeletionRequested = createAction("user/cancelScheduledDeletionRequested")
export const cancelScheduledDeletionReceived = createAction("user/cancelScheduledDeletionReceived")
export const cancelScheduledDeletionFailed = createAction("user/cancelScheduledDeletionFailed")

export const requestedUpdatePaymentMethodExpiry = createAction("user/requestedUpdatePaymentMethodExpiry")
export const receivedUpdatePaymentMethodExpiry = createAction("user/receivedUpdatePaymentMethodExpiry")
export const failedUpdatePaymentMethodExpiry = createAction("user/failedUpdatePaymentMethodExpiry")

const customBrandingUpdateRequested = createAction("user/customBrandingUpdateRequested")
const customBrandingUpdated = createAction("user/customBrandingUpdated")
const customBrandingUpdateFailed = createAction("user/customBrandingUpdateFailed")

const initialState = {
  emailConfirmation: null,
  selectedCardLast4: null,
  ...LOADING_INITIAL_STATE,
  id: null,
  first_name: "",
  last_name: "",
  email: "",
  avatar: "",
  gender: "",
  phone_number: "",
  birthday: "",
  settings: "",
  save_stories_to_profile: false,
  save_stories_to_computer: false,
  hide_stories_from: [],
  codeSent: false,
  forceStatus: false,
  role: "",
  is_sportsman: true,
  permissions: {},
  active_profile: null,
  guide: {
    ...LOADING_INITIAL_STATE,
    id: null,
    active: false,
    activity_type: "",
    account_status: "",
    user: {
      first_name: "",
      last_name: ""
    },
    display_name: "",
    email: "",
    phone_number: "",
    company_name: "",
    website: "",
    description: "",
    street_address: "",
    address_line_2: "",
    state: "",
    country: "",
    city: "",
    zip_code: "",
    expertises: [],
    photos: [],
    upload_client_database: false,
    operating_location: "",
    documents: [],
    wizard_step: null,
    wizard_completed: null,
    unavailable_weekdays: {
      sunday: false,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false
    },
    stripe_account_data: null,
    paypal_account_data: { is_connected: false },
    custom_branding: { enabled: false, logo_url: null },
    last_booking_date: null,
    scheduled_deletion_date: null,
    verification_status: "",
    timezone: ""
  },
  selectedPaymentMethodId: null,
  payment_methods: {
    ...LOADING_INITIAL_STATE,
    items: []
  },
  sendOtpToVerifyPhone: {
    ...LOADING_INITIAL_STATE
  },
  verifyOtpForPhoneNumberUpdation: {
    ...LOADING_INITIAL_STATE
  },
  is_password: false,
  connected_oauth_providers: [],
  sendOtpToVerifyEmail: {
    ...LOADING_INITIAL_STATE
  },
  verifyOtpForEmailUpdation: {
    ...LOADING_INITIAL_STATE
  },
  sendOtpForAccountAction: {
    ...LOADING_INITIAL_STATE
  },
  updateUserInfo: {
    ...LOADING_INITIAL_STATE
  },
  checkUserDeleteAble: {
    ...LOADING_INITIAL_STATE
  },
  deleteAccount: {
    ...LOADING_INITIAL_STATE
  },
  updateGuideAccountStatus: {
    ...LOADING_INITIAL_STATE
  },
  scheduleDeletion: {
    ...LOADING_INITIAL_STATE
  },
  cancelScheduledDeletion: {
    ...LOADING_INITIAL_STATE
  },
  detachPaymentMethod: {
    ...LOADING_INITIAL_STATE
  },
  updatePaymentMethodExpiry: {
    ...LOADING_INITIAL_STATE
  }
}

const userReducer = createReducer(initialState, {
  [setSelectedPaymentMethodId.type]: (state, action) => {
    state.selectedPaymentMethodId = action.payload
  },
  [emailConfirmationReceived.type]: (state, action) => {
    state.emailConfirmation = action.payload
  },
  [setSelectedCardLast4.type]: (state, action) => {
    state.selectedCardLast4 = action.payload
  },
  [requested.type]: (state) => {
    setRequested(state)
    if (state.guide) setRequested(state.guide)
  },
  [received.type]: (state, action) => {
    const payload = action.payload
    Object.assign(state, payload)
    if (payload.permissions) {
      state.permissions = payload.permissions
    }
    if (payload.active_profile) {
      state.active_profile = payload.active_profile
    }
    // Map API's time_zone to Redux timezone field for guide
    if (payload.guide?.time_zone) {
      state.guide.timezone = payload.guide.time_zone
    }
    setReceived(state)
    if (state.guide) setReceived(state.guide)
  },
  [updated.type]: (state, { payload }) => {
    const guide = (state.guide || payload?.user?.guide) && { ...state.guide, ...payload?.user?.guide }
    Object.assign(state, { ...payload?.user, ...(guide && { guide }) })
    setReceived(state)
    if (state.guide) setReceived(state.guide)
  },
  [failed.type]: (state) => {
    setFailed(state)
    if (state.guide) setFailed(state.guide)
  },
  [requestedPaymentMethods.type]: (state) => {
    setRequested(state.payment_methods)
  },
  [receivedPaymentMethods.type]: (state, action) => {
    const items = action.payload?.payment_methods || action.payload || []
    state.payment_methods.items = items
    if (!state.selectedPaymentMethodId && Array.isArray(items) && items.length > 0) {
      state.selectedPaymentMethodId = items[0].payment_method_id
    }
    setReceived(state.payment_methods)
  },
  [failedPaymentMethods.type]: (state) => {
    setFailed(state.payment_methods)
    state.payment_methods.items = []
  },
  [cleanedPaymentMethods.type]: (state) => {
    state.payment_methods = initialState.payment_methods
    state.selectedPaymentMethodId = initialState.selectedPaymentMethodId
  },
  [stripeDisconnected.type]: (state, action) => {
    setReceived(state)
    Object.assign(state.guide, action.payload)
  },
  [cleaned.type]: () => initialState,
  [passwordUpdated.type]: (state) => {
    setReceived(state)
  },
  [guideUpdated.type]: (state, action) => {
    const { first_name, last_name } = action.payload.user
    Object.assign(state, { first_name, last_name })
    Object.assign(state.guide, action.payload)
    setReceived(state)
    if (state.guide) setReceived(state.guide)
  },
  [customBrandingUpdateRequested.type]: (state) => {
    setRequested(state.guide)
  },
  [customBrandingUpdated.type]: (state, action) => {
    Object.assign(state.guide.custom_branding, action.payload)
    setReceived(state.guide)
  },
  [customBrandingUpdateFailed.type]: (state) => {
    setFailed(state.guide)
  },
  [guideUnavailableWeekdaysUpdated.type]: (state, action) => {
    Object.assign(state.guide, action.payload)
    setReceived(state)
    if (state.guide) setReceived(state.guide)
  },
  [statusUpdated.type]: (state, action) => {
    Object.assign(state, action.payload)
    setReceived(state)
    if (state.guide) setReceived(state.guide)
  },
  [forceStatusUpdated.type]: (state, action) => {
    Object.assign(state, action.payload)
    state.forceStatus = ["offline", "away"].includes(action.payload.status)
    setReceived(state)
    if (state.guide) setReceived(state.guide)
  },
  [userVerified.type]: (state, action) => {
    Object.assign(state, action.payload)
    state.codeSent = true
  },
  [userVerificationError.type]: (action) => {
    toaster.error(i18n.t(action.payload.message))
  },
  [oAuthDisconnected.type]: (state, { props }) => {
    setReceived(state)
    state.connected_oauth_providers = state.connected_oauth_providers.filter((provider) => provider !== props.provider)
  },
  [sendOtpToVerifyPhoneRequested.type]: (state) => {
    setRequested(state.sendOtpToVerifyPhone)
  },
  [sendOtpToVerifyPhoneReceived.type]: (state) => {
    setReceived(state.sendOtpToVerifyPhone)
  },
  [sendOtpVerifyPhoneFailed.type]: (state) => {
    setFailed(state.sendOtpToVerifyPhone)
  },
  [verifyOtpForPhoneNumberUpdationRequested.type]: (state) => {
    setRequested(state.verifyOtpForPhoneNumberUpdation)
  },
  [verifyOtpForPhoneNumberUpdationReceived.type]: (state, action) => {
    setReceived(state.verifyOtpForPhoneNumberUpdation)
    state.phone_number = action.payload?.user?.phone_number
  },
  [verifyOtpForPhoneNumberUpdationFailed.type]: (state) => {
    setFailed(state.verifyOtpForPhoneNumberUpdation)
  },
  [sendOtpToVerifyEmailRequested.type]: (state) => {
    setRequested(state.sendOtpToVerifyEmail)
  },
  [sendOtpToVerifyEmailReceived.type]: (state) => {
    setReceived(state.sendOtpToVerifyEmail)
  },
  [sendOtpVerifyEmailFailed.type]: (state) => {
    setFailed(state.sendOtpToVerifyEmail)
  },
  [verifyOtpForEmailUpdationRequested.type]: (state) => {
    setRequested(state.verifyOtpForEmailUpdation)
  },
  [verifyOtpForEmailUpdationReceived.type]: (state, action) => {
    setReceived(state.verifyOtpForEmailUpdation)
    state.email = action.payload?.user?.email
  },
  [verifyOtpForEmailUpdationFailed.type]: (state) => {
    setFailed(state.verifyOtpForEmailUpdation)
  },
  [sendOtpForAccountActionRequested.type]: (state) => {
    setRequested(state.sendOtpForAccountAction)
  },
  [sendOtpForAccountActionReceived.type]: (state) => {
    setReceived(state.sendOtpForAccountAction)
  },
  [sendOtpForAccountActionFailed.type]: (state) => {
    setFailed(state.sendOtpForAccountAction)
  },
  [updateUserInfoRequested.type]: (state) => {
    setRequested(state.updateUserInfo)
  },
  [updateUserInfoReceived.type]: (state) => {
    setReceived(state.updateUserInfo)
  },
  [updateUserInfoFailed.type]: (state) => {
    setFailed(state.updateUserInfo)
  },
  [checkUserDeleteAbleRequested.type]: (state) => {
    setRequested(state.checkUserDeleteAble)
  },
  [checkUserDeleteAbleReceived.type]: (state, { payload }) => {
    setReceived(state.checkUserDeleteAble)
    if (payload?.status_code === 103) {
      state.guide.last_booking_date = payload?.last_booking_date
    }
  },
  [checkUserDeleteAbleFailed.type]: (state) => {
    setFailed(state.checkUserDeleteAble)
  },
  [deleteAccountRequested.type]: (state) => {
    setRequested(state.deleteAccount)
  },
  [deleteAccountReceived.type]: (state) => {
    setReceived(state.deleteAccount)
  },
  [deleteAccountFailed.type]: (state) => {
    setFailed(state.deleteAccount)
  },
  [updateGuideAccountStatusRequested.type]: (state) => {
    setRequested(state.updateGuideAccountStatus)
  },
  [updateGuideAccountStatusReceived.type]: (state) => {
    setReceived(state.updateGuideAccountStatus)
    state.guide.account_status = state.guide?.account_status === "deactivated" ? "activated" : "deactivated"
  },
  [updateGuideAccountStatusFailed.type]: (state) => {
    setFailed(state.updateGuideAccountStatus)
  },
  [scheduleDeletionRequested.type]: (state) => {
    setRequested(state.scheduleDeletion)
  },
  [scheduleDeletionReceived.type]: (state) => {
    setReceived(state.scheduleDeletion)
  },
  [scheduleDeletionFailed.type]: (state) => {
    setFailed(state.scheduleDeletion)
  },
  [cancelScheduledDeletionRequested.type]: (state) => {
    setRequested(state.cancelScheduledDeletion)
  },
  [cancelScheduledDeletionReceived.type]: (state) => {
    setReceived(state.cancelScheduledDeletion)
  },
  [cancelScheduledDeletionFailed.type]: (state) => {
    setFailed(state.cancelScheduledDeletion)
  },
  [requestedDetachPaymentMethods.type]: (state) => {
    setRequested(state.detachPaymentMethod)
  },
  [receivedDetachPaymentMethods.type]: (state) => {
    setReceived(state.detachPaymentMethod)
  },
  [failedDetachPaymentMethods.type]: (state) => {
    setFailed(state.detachPaymentMethod)
  },
  [requestedUpdatePaymentMethodExpiry.type]: (state) => {
    setRequested(state.updatePaymentMethodExpiry)
  },
  [receivedUpdatePaymentMethodExpiry.type]: (state, { payload }) => {
    setReceived(state.updatePaymentMethodExpiry)
    const responsePaymentMethod = payload?.payment_method
    const items = state.payment_methods?.items
    if (responsePaymentMethod && Array.isArray(items)) {
      const target = items.find((item) => item.id === responsePaymentMethod.id)
      if (target) {
        if (responsePaymentMethod.exp_month !== undefined) target.exp_month = responsePaymentMethod.exp_month
        if (responsePaymentMethod.exp_year !== undefined) target.exp_year = responsePaymentMethod.exp_year
      }
    }
  },
  [failedUpdatePaymentMethodExpiry.type]: (state) => {
    setFailed(state.updatePaymentMethodExpiry)
  }
})

export default userReducer

export const getUser = () =>
  api({
    url: "/user",
    method: "get",
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type,
    params: { include_metadata: true }
  })

export const cleanUser = () => cleaned()

export const updateUser = (data) =>
  api({
    url: "/user",
    method: "put",
    data: data,
    onStart: requested.type,
    onSuccess: updated.type,
    onError: failed.type
  })

export const updateUserStatus =
  (data, force = false) =>
  (dispatch, getState) => {
    const state = getState()
    const { forceStatus } = state.user
    if (forceStatus && !force) return

    dispatch(
      api({
        url: "/user/update_status",
        method: "put",
        data: data,
        onStart: requested.type,
        onSuccess: force ? forceStatusUpdated.type : statusUpdated.type,
        onError: failed.type
      })
    )
  }

export const updateGuide = (guideId, data) =>
  api({
    url: `/guides/${guideId}`,
    method: "put",
    data: data,
    onStart: requested.type,
    onSuccess: guideUpdated.type,
    onError: failed.type
  })

export const updateUnavailableWeekdays = (guideId, data) => {
  const formData = serialize({ guide: pick(data, "unavailable_weekdays") })
  return api({
    url: `/guides/${guideId}`,
    method: "put",
    data: formData,
    onStart: requested.type,
    onSuccess: guideUnavailableWeekdaysUpdated.type,
    onError: failed.type
  })
}

export const updatePassword = (data) =>
  api({
    url: "/user/update_password",
    method: "put",
    data: data,
    onStart: requested.type,
    onSuccess: passwordUpdated.type,
    onError: failed.type
  })

export const verifyPhone = (phoneNumber) =>
  api({
    url: "/user/verify_phone",
    method: "post",
    data: phoneNumber,
    onSuccess: userVerified.type,
    onError: userVerificationError.type
  })

export const verifyCode = (code) =>
  api({
    url: "/user/verify_code",
    method: "post",
    data: code,
    onSuccess: userVerified.type,
    onError: userVerificationError.type
  })

export const getPaymentMethods = (id) =>
  api({
    url: `/users/${id}/payment_methods`,
    onStart: requestedPaymentMethods.type,
    onSuccess: receivedPaymentMethods.type,
    onError: failedPaymentMethods.type
  })

export const attachPaymentMethod = (payload) =>
  api({
    url: "/user/attach_payment_method",
    method: "put",
    data: payload,
    onStart: requestedPaymentMethods.type,
    onSuccess: receivedPaymentMethods.type,
    onError: failedPaymentMethods.type
  })

// `guestToken`: the booking widget's order token (an order's token isn't stored for later calls);
// without it the request carries the stored token, as before.
export const attachUserPaymentMethod = (param, { guestToken } = {}) =>
  api({
    url: "/user/attach_payment_method_for_user",
    method: "put",
    data: param,
    guestToken,
    onStart: requestedPaymentMethods.type,
    onSuccess: receivedPaymentMethods.type,
    onError: failedPaymentMethods.type
  })

export const detachPaymentMethod = (payment_method_id) =>
  api({
    url: "/user/detach_payment_method",
    method: "put",
    data: { payment_method_id },
    onStart: requestedDetachPaymentMethods.type,
    onSuccess: receivedDetachPaymentMethods.type,
    onError: failedDetachPaymentMethods.type
  })
export const updatePaymentMethodExpiry = (payload) =>
  api({
    url: "/user/update_payment_method_expiry",
    method: "patch",
    data: payload,
    onStart: requestedUpdatePaymentMethodExpiry.type,
    onSuccess: receivedUpdatePaymentMethodExpiry.type,
    onError: failedUpdatePaymentMethodExpiry.type
  })

export const stripeConnect = (dispatch) =>
  dispatch(
    api({
      url: "/guide/stripe_accounts/connect",
      method: "post",
      data: { return_url: window?.location?.href, refresh_url: window?.location?.href },
      onStart: requested.type,
      onError: failed.type
    })
  ).then((res) => res?.url && window?.location?.replace(res?.url))

export const stripeDisconnect = () =>
  api({
    url: "/guide/stripe_accounts/disconnect",
    method: "put",
    onStart: requested.type,
    onSuccess: stripeDisconnected.type,
    onError: failed.type
  })

export const platformConnect = (platform) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/platform_accounts/${platform}/connect`,
      method: "post",
      onStart: requested.type,
      onSuccess: failed.type,
      onError: failed.type,
      props: { showDetailedError: true }
    })
  ).then((res) => res?.url && window?.location?.replace(res?.url))

export const platformConnectConfirm = (platform, { state, code }) =>
  api({
    url: `/guide/platform_accounts/${platform}/confirm`,
    method: "post",
    data: { platform_account: { state, code } },
    onStart: requested.type,
    onSuccess: guideUpdated.type,
    onError: failed.type,
    props: { showDetailedError: true }
  })

export const platformDisconnect = (platform) =>
  api({
    url: `/guide/platform_accounts/${platform}/disconnect`,
    method: "delete",
    onStart: requested.type,
    onSuccess: guideUpdated.type,
    onError: failed.type,
    props: { showDetailedError: true }
  })

export const connectOAuthProvider = (data) => (dispatch) =>
  dispatch(
    api({
      url: `/user/connect_oauth_provider`,
      method: "post",
      data,
      onStart: requested.type,
      onSuccess: updated.type,
      onError: failed.type
    })
  )

export const disconnectOAuthProvider = (provider) =>
  api({
    url: `/user/disconnect_oauth_provider`,
    method: "delete",
    data: { provider },
    onStart: requested.type,
    onSuccess: oAuthDisconnected.type,
    onError: failed.type,
    props: { showToast: false, provider }
  })

export const emailConfirmation = (data) =>
  api({
    url: `/guide/bookings/${data.id}/send_reminder`,
    method: "post",
    data: { id: data.id },
    onStart: requested.type,
    onSuccess: emailConfirmationReceived.type,
    onError: failed.type,
    props: { showToast: false }
  })

export const updatePaymentMethod = (payload) =>
  api({
    url: "/user/update_payment_method",
    method: "put",
    data: payload,
    onStart: requestedPaymentMethods.type,
    onSuccess: receivedPaymentMethods.type,
    onError: failedPaymentMethods.type
  })

// SELECTORS
export const guideSelector = createSelector(
  (state) => state.user,
  (user) => user.guide
)

export const guideActivityTypeSelector = createSelector(
  (state) => state.user.guide,
  (guide) => guide.activity_type
)

export const isGuideCompletedSelector = createSelector(
  (state) => state.user.guide || {},
  (guide) => guide.wizard_completed
)

export const isGuideSelector = createSelector(
  (state) => state.user.role,
  (role) => role === "guide_user"
)

export const isUserSelector = createSelector(
  (state) => state.user.role,
  (role) => role === "user"
)

export const defaultPaymentMethodSelector = createSelector(
  (state) => state.user,
  (user) => user.default_payment_method_id || null
)

export const paymentMethodsSelector = createSelector(
  (state) => state.user.payment_methods,
  (payment_methods) => payment_methods?.items || []
)

export const paymentMethodsLoadingSelector = createSelector(
  (state) => state.user.payment_methods,
  (payment_methods) => payment_methods?.loading || false
)

export const stripeConnectedSelector = createSelector(
  (state) => state.user.guide,
  (guide) => guide.stripe_account_data || {}
)
export const isStripeConnectedSelector = createSelector(
  stripeConnectedSelector,
  (stripe_account_data) => !!Object.keys(stripe_account_data).length
)
export const isStripeChargesEnabledSelector = createSelector(
  stripeConnectedSelector,
  (stripe_account_data) => stripe_account_data.charges_enabled
)
export const isStripePayoutsEnabledSelector = createSelector(
  stripeConnectedSelector,
  (stripe_account_data) => stripe_account_data.payouts_enabled
)
export const isStripePayoutsEmail = createSelector(stripeConnectedSelector, (stripe_account_data) => stripe_account_data.stripe_email)
export const errorStripeMessage = createSelector(stripeConnectedSelector, (stripe_account_data) => stripe_account_data.error_message)
export const stripeLinkSelector = createSelector(stripeConnectedSelector, (stripe_account_data) => stripe_account_data.stripe_link)
export const checkStripeFullyConnected = (stripeAccountData) =>
  !!stripeAccountData?.payouts_enabled && !!stripeAccountData?.charges_enabled && !stripeAccountData?.error_message
export const isStripeFullyConnectedSelector = createSelector(stripeConnectedSelector, checkStripeFullyConnected)
export const selectedPaymentMethodIdSelector = createSelector(
  (state) => state.user,
  (user) => user.selectedPaymentMethodId
)
export const selectedCardLast4Selector = createSelector(
  (state) => state.user,
  (user) => user.selectedCardLast4
)

export const sendOtpForAccountActionLoadingSelector = createSelector(
  (state) => state.user.sendOtpForAccountAction,
  (sendOtpForAccountAction) => sendOtpForAccountAction.loading
)

export const sendOtpToVerifyPhone = (phoneNumber) =>
  api({
    url: "/user/send_otp_for_phone_number_update",
    method: "post",
    data: phoneNumber,
    onStart: sendOtpToVerifyPhoneRequested.type,
    onSuccess: sendOtpToVerifyPhoneReceived.type,
    onError: sendOtpVerifyPhoneFailed.type
  })

export const verifyOtpForPhoneNumberUpdation = (data) =>
  api({
    url: "/user/verify_otp_on_phone_number_updation",
    method: "put",
    data: data,
    onStart: verifyOtpForPhoneNumberUpdationRequested.type,
    onSuccess: verifyOtpForPhoneNumberUpdationReceived.type,
    onError: verifyOtpForPhoneNumberUpdationFailed.type
  })

export const sendOtpToVerifyEmail = (email) =>
  api({
    url: "/user/send_otp_for_email_update",
    method: "post",
    data: { email },
    onStart: sendOtpToVerifyEmailRequested.type,
    onSuccess: sendOtpToVerifyEmailReceived.type,
    onError: sendOtpVerifyEmailFailed.type
  })

export const verifyOtpForEmailUpdation = ({ email, otp }) =>
  api({
    url: "/user/verify_otp_on_email_updation",
    method: "put",
    data: { email, otp },
    onStart: verifyOtpForEmailUpdationRequested.type,
    onSuccess: verifyOtpForEmailUpdationReceived.type,
    onError: verifyOtpForEmailUpdationFailed.type
  })

export const sendOtpForAccountAction = () =>
  api({
    url: "/user/send_otp_for_account_action",
    method: "get",
    onStart: sendOtpForAccountActionRequested.type,
    onSuccess: sendOtpForAccountActionReceived.type,
    onError: sendOtpForAccountActionFailed.type
  })

export const isPlatformConnectedSelector = (platform) =>
  createSelector(
    (state) => state.user.guide[`${platform}_account_data`] || {},
    (platform_account_data) => !!Object.keys(platform_account_data).length
  )

export const updateUserInfo = (user) =>
  api({
    url: "/user/update_user_info",
    method: "put",
    data: user,
    onStart: updateUserInfoRequested.type,
    onSuccess: updateUserInfoReceived.type,
    onError: updateUserInfoFailed.type,
    noAuth: true
  })

export const checkUserDeleteAble = () =>
  api({
    url: `/user/check_deletable`,
    method: "get",
    onStart: checkUserDeleteAbleRequested.type,
    onSuccess: checkUserDeleteAbleReceived.type,
    onError: checkUserDeleteAbleFailed.type
  })

export const deleteAccount = (data) =>
  api({
    url: "/user",
    method: "delete",
    data,
    onStart: deleteAccountRequested.type,
    onSuccess: deleteAccountReceived.type,
    onError: deleteAccountFailed.type
  })

export const scheduleDeletion = (data) => (dispatch) =>
  dispatch(
    api({
      url: "/guides/schedule_deletion",
      method: "put",
      data,
      onStart: scheduleDeletionRequested.type,
      onSuccess: scheduleDeletionReceived.type,
      onError: scheduleDeletionFailed.type
    })
  ).then((res) => {
    dispatch(getUser())
    return res
  })

export const updateGuideAccountStatus = (statusData) =>
  api({
    url: "/guides/account_status",
    method: "put",
    data: statusData,
    onStart: updateGuideAccountStatusRequested.type,
    onSuccess: updateGuideAccountStatusReceived.type,
    onError: updateGuideAccountStatusFailed.type
  })

export const cancelScheduledDeletion = (statusData) => (dispatch) =>
  dispatch(
    api({
      url: "/guides/cancel_schedule_deletion",
      method: "put",
      data: statusData,
      onStart: cancelScheduledDeletionRequested.type,
      onSuccess: cancelScheduledDeletionReceived.type,
      onError: cancelScheduledDeletionFailed.type
    })
  ).then((res) => {
    dispatch(getUser())
    return res
  })

export { received as receivedUser, guideUnavailableWeekdaysUpdated }

export const isPaypalConnectedSelector = createSelector(
  (state) => state.user?.guide?.paypal_account_data || {},
  (paypal_account_data) => paypal_account_data?.is_connected
)

export const paypalAccountDataSelector = (state) => state.user?.guide?.paypal_account_data || {}

export const userPermissionsSelector = (state) => state.user?.permissions || {}
export const activeProfileSelector = (state) => state.user?.active_profile || null
export const hasFullAccessSelector = (state) => state.user?.active_profile?.has_full_access || false

